import { t } from "i18next";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Video } from "../../../../interfaces/video";
import { emptyTrainingSessionPhase, TrainingSessionPhase, TrainingSessionRecipient } from "../../../../interfaces/training-session";
import { getTeamOptions } from "../../../../services/team";
import { Team } from "../../../../interfaces/team";
import { getAthleteOptions } from "../../../../services/athlete";
import { getTrainingSessionPhaseSingle, updateTrainingSessionPhase } from "../../../../services/training-session";
import { LoadingSpinner } from "../../../general/loading-spinner";

interface CoachTrainingSessionPhaseEditProps {
    id: number;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

export function CoachTrainingSessionPhaseEdit(props: CoachTrainingSessionPhaseEditProps) {
    const [loaded, setLoaded] = useState(false);
    const [trainingSessionPhase, setTrainingSessionPhase] = useState<TrainingSessionPhase>(emptyTrainingSessionPhase);
    const [videos, setVideos] = useState<any[]>([]);
    const [recipients, setRecipients] = useState<TrainingSessionRecipient[]>([]);
    const [showAddNewRecipient, setShowAddNewRecipient] = useState(false);
    const [newRecipientTypeSelected, setNewRecipientTypeSelected] = useState('');
    const [newRecipientTeamSelected, setNewRecipientTeamSelected] = useState('');
    const [newRecipientAthleteSelected, setNewRecipientAthleteSelected] = useState('');
    const [teamOptions, setTeamOptions] = useState<any[]>([]);
    const [athleteOptions, setAthleteOptions] = useState<any[]>([]);

    useEffect(() => {
        Promise.all([
            getTrainingSessionPhaseSingle(props.id),
            getTeamOptions(),
            getAthleteOptions()
        ]).then((response: any) => {
            setTrainingSessionPhase(response[0].data);

            const newVideos = response[0].data.videos.map((v: Video) => {
                return {
                    value: v.id,
                    label: v.name
                }
            });
            setVideos(newVideos);

            const newRecipients = response[0].data.recipients.map((r: any) => {
                return r;
            });
            setRecipients(newRecipients);

            setTeamOptions(response[1].data.map((t: Team) => {
                return {
                    value: t.id,
                    label: t.name
                }
            }));
            setAthleteOptions(response[2].data.map((t: Team) => {
                return {
                    value: t.id,
                    label: t.name
                }
            }));
            setLoaded(true);
        });
    }, []);

    const handleSave = (event: any) => {
        event.preventDefault();

        const body = {
            id: event.target.id.value,
            name: event.target.name.value,
            description: event.target.description.value,
            goals: event.target.goals.value,
            recipients: recipients,
            videos: videos.map((v: any) => { return v.value })
        }
        updateTrainingSessionPhase(body)
            .then(response => {
                if (response.data.status == 1) {
                    props.onSave(response.data.data);
                } else {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }
    
    const handleClose = () => {
        props.onClose();
    }

    const handleAddNewPhase = (event: any) => {
        event.preventDefault();
        setShowAddNewRecipient(true);
    }

    const handleNewRecipientTypeChange = (event: any) => {
        setNewRecipientTypeSelected(event.target.value);
    }

    const handleNewRecipientTeamChange = (event: any) => {
        setNewRecipientTeamSelected(event.target.value);
    }

    const handleNewRecipientAthleteChange = (event: any) => {
        setNewRecipientAthleteSelected(event.target.value);
    }

    const handleCloseAddNewRecipient = () => {
        setShowAddNewRecipient(false);
    }

    const handleAddNewRecipient = (event: any) => {
        event.preventDefault();
        const index = recipients.findIndex((i: any) => (i.type == newRecipientTypeSelected && (i.reference == newRecipientTeamSelected || i.reference == newRecipientAthleteSelected)));
        if (index == -1) {
            const newRecipients = [
                ...recipients,
                {
                    type: newRecipientTypeSelected,
                    reference: newRecipientTypeSelected == 'team' ? newRecipientTeamSelected : newRecipientAthleteSelected,
                    label: ''
                }
            ];
            setRecipients(newRecipients);
            setShowAddNewRecipient(false);
            setNewRecipientAthleteSelected('');
            setNewRecipientTeamSelected('');
            setNewRecipientTypeSelected('');
        } else {
            alert(t("reference_already_assigned"));
        }
        return false;
    }

    const referenceLabel = (type: string, reference: string) => {
        let reference_label = '';
        switch (type) {
            case 'team':
                const indexTeam = teamOptions.findIndex((t: any) => t.value == reference);
                if (indexTeam !== -1) {
                    reference_label = teamOptions[indexTeam].label;
                }
                break;
            case 'athlete':
                const indexAthlete = athleteOptions.findIndex((t: any) => t.value == reference);
                if (indexAthlete !== -1) {
                    reference_label = athleteOptions[indexAthlete].label;
                }
                break;
        }
        return reference_label;
    };

    const handleDeleteRecipient = (event: any) => {
        event.preventDefault();
        const type = event.currentTarget.dataset.type;
        const reference = event.currentTarget.dataset.reference;
        const newRecipients = recipients.filter((r: any) => !(r.type == type && r.reference == reference));
        setRecipients(newRecipients);
    }

    return (
        <Modal className="training-session-phase-edit-container" show={ true }
            onHide={ handleClose } size="xl"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("edit_training_session_phase") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded && 
                    <LoadingSpinner />
                }
                { loaded &&
                    <Form onSubmit={ handleSave } className="new-trainining-session-phase-form">
                        <div className="fields">
                            <Form.Control type="hidden" name="id" defaultValue={ trainingSessionPhase.id } />
                            <Container>
                                <Row>
                                    <Col lg={6}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label aria-required="true">{ t("Name") }</Form.Label>
                                            <Form.Control type="text" name="name" required defaultValue={ trainingSessionPhase.name } />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="description">
                                            <Form.Label>{ t("description") }</Form.Label>
                                            <Form.Control name="description" as="textarea" rows={5} defaultValue={ trainingSessionPhase.description } />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="goals">
                                            <Form.Label>{ t("goals") }</Form.Label>
                                            <Form.Control name="goals" as="textarea" rows={5} defaultValue={ trainingSessionPhase.goals } />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3 recipients" controlId="recipients">
                                            <Form.Label>{ t("recipients_training") }</Form.Label>
                                            <div className="recipients-list">
                                                { recipients.length == 0 &&
                                                    <div className="recipients-message">
                                                        { t("training_session_no_recipients") }
                                                    </div>
                                                }
                                                {
                                                    recipients.map((r: TrainingSessionRecipient) => {
                                                        return <div className="recipient-item">
                                                            { t(r.type) + ': ' + referenceLabel(r.type, r.reference) }
                                                            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDeleteRecipient }
                                                                data-type={ r.type } data-reference={ r.reference }>
                                                                <i className="bi bi-trash"></i>
                                                            </a>
                                                        </div>
                                                    })
                                                }
                                            </div>
                                            { !showAddNewRecipient && <a className="add-new-recipient" href="#" onClick={ handleAddNewPhase }>
                                                <i className="bi bi-plus-circle"></i> { t("add_athletes")}
                                            </a>}
                                            { showAddNewRecipient && 
                                                <div className="add-new-recipient-form">
                                                    <a href="#" className="close" onClick={ handleCloseAddNewRecipient }></a>
                                                    <Form.Select aria-label={ t("type") } name="type" onChange={ handleNewRecipientTypeChange }>
                                                        <option>{ t("select_option") }</option>
                                                        <option key="team" value="team">{ t("team") }</option>
                                                        <option key="athlete" value="athlete">{ t("athlete") }</option>
                                                    </Form.Select>
                                                    { newRecipientTypeSelected == 'team' && 
                                                        <Form.Select aria-label={ t("team") } name="team" onChange={ handleNewRecipientTeamChange }>
                                                            <option>{ t("select_team") }</option>
                                                            { teamOptions.map((t: any) => {
                                                                return <option key={ 'team-' + t.value } value={ t.value }>{ t.label }</option>
                                                            })}
                                                        </Form.Select>
                                                    }
                                                    { newRecipientTypeSelected == 'athlete' && 
                                                        <Form.Select aria-label={ t("athlete") } name="athlete" onChange={ handleNewRecipientAthleteChange }>
                                                            <option>{ t("select_athlete") }</option>
                                                            { athleteOptions.map((t: any) => {
                                                                return <option key={ 'athlete-' + t.value } value={ t.value }>{ t.label }</option>
                                                            })}
                                                        </Form.Select>
                                                    }
                                                    <div className="actions">
                                                        <Button type="submit" variant="primary" onClick={ handleAddNewRecipient }>
                                                            { t("add") }
                                                        </Button>
                                                    </div>
                                                </div>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Container>
                        </div>
                        <div className="actions">
                            <Button type="submit" variant="primary">
                                { t("Save") }
                            </Button>
                            <Button variant="secondary" onClick={ handleClose }>
                                { t("Close") }
                            </Button>
                        </div>
                    </Form>
                }
            </Modal.Body>
        </Modal>
    );
}
